import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridSortModel,
  getGridStringOperators,
} from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fetchSites } from 'store/sitesSlice';
import {
  fetchPromoCodes,
  setParams,
  createPromoCode,
  deletePromoCode,
  updatePromoCode,
} from 'store/promoCodesSlice';
import { RootState } from 'store/index';
import { commonGridStyles } from 'styles/gridStyles';
import commonStyles from 'styles/common.module.scss';
import { formatDate } from 'utils/date';
import ConfirmModal from 'components/modals/ConfirmModal';
import PromoCodeDetailsModal from './PromoCodeDetailsModal';
import PromoCodeModal from './PromoCodeModal';
import SiteSelector from 'components/common/SiteSelector/SiteSelector';
import CustomPagination from 'components/common/pagination/pagination';
import { ROWS_PER_PAGE_OPTIONS } from 'constants/pagination';
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react';
import { Alert } from 'components/alert/alert';
import { PromoCode } from 'types/promoCode';
import { useNavigate } from 'react-router-dom';
import styles from './PromoCodes.module.scss';

const STATUS_OPTIONS = ['active', 'exhausted', 'expired'];

const PromoCodes: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [viewingUsagesForPromo, setViewingUsagesForPromo] = useState<PromoCode | null>(null);
  const { data: sites, isLoading: sitesLoading } = useAppSelector(
    (state: RootState) => state.sites,
  );
  const {
    data: promoCodes,
    total,
    isLoading,
    params,
    isCreating,
  } = useAppSelector((state: RootState) => state.promoCodes);
  const theme = useAppSelector((state: RootState) => state.ui.theme);

  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const customStringOperators = getGridStringOperators().filter(
    (operator) => operator.value === 'contains',
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      setFilterModel(model);

      // Map current filter items to query parameters
      const newFilters: Record<string, any> = {};

      // Clear previous filters by setting them to undefined
      if (params) {
        Object.keys(params).forEach((key) => {
          if (
            key !== 'page' &&
            key !== 'limit' &&
            key !== 'sortField' &&
            key !== 'sortValue' &&
            key !== 'siteId' &&
            key !== 'status'
          ) {
            newFilters[key] = undefined;
          }
        });
      }

      // Set new filter values
      model.items.forEach((item) => {
        if (item.field && item.value !== undefined && item.value !== '') {
          newFilters[item.field] = item.value;
        }
      });

      dispatch(setParams({ ...newFilters, page: 1 }));
    },
    [dispatch, params],
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        dispatch(
          setParams({
            sortField: model[0].field,
            sortValue: model[0].sort?.toUpperCase() as 'ASC' | 'DESC',
          }),
        );
      }
    },
    [dispatch],
  );

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<PromoCode | null>(null);
  const [modalError, setModalError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; type: 'success' | 'error' } | null>(
    null,
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Modals state
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [detailsData, setDetailsData] = useState<{
    code: string;
    history: any[];
  } | null>(null);

  const showFeedback = useCallback((message: string, type: 'success' | 'error') => {
    setFeedback({ message, type });
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setFeedback(null);
    }, 3000);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (sites.length === 0) {
      dispatch(fetchSites({ page: 1, limit: 100, sortField: 'id', sortValue: 'ASC' }));
    }
  }, [dispatch, sites.length]);

  useEffect(() => {
    if (sites.length > 0 && isInitialLoad && params.siteId === '') {
      const dgentaSite = sites.find((s) => s.title.toLowerCase().includes('dgenta'));
      if (dgentaSite) {
        dispatch(setParams({ siteId: dgentaSite.id }));
        setIsInitialLoad(false);
      }
    }
  }, [sites, isInitialLoad, params.siteId, dispatch]);

  useEffect(() => {
    dispatch(fetchPromoCodes(params));
  }, [dispatch, params]);

  const handleSiteChange = (siteId: string | number) => {
    dispatch(setParams({ siteId: siteId === 'all' ? undefined : siteId, page: 1 }));
  };

  const handleStatusChange = (status: string) => {
    dispatch(setParams({ status: status === 'all' ? undefined : status, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    dispatch(setParams({ page }));
  };

  const handlePageSizeChange = (limit: number) => {
    dispatch(setParams({ limit, page: 1 }));
  };

  const handleModalSubmit = async (data: any) => {
    setModalError(null);
    try {
      if (editingPromo) {
        const resultAction = await dispatch(updatePromoCode({ id: editingPromo.id, data }));
        if (updatePromoCode.fulfilled.match(resultAction)) {
          showFeedback('Promo code updated successfully', 'success');
          setIsModalOpen(false);
          setEditingPromo(null);
        } else {
          setModalError((resultAction.payload as string) || 'Failed to update promo code');
        }
      } else {
        const resultAction = await dispatch(createPromoCode(data));
        if (createPromoCode.fulfilled.match(resultAction)) {
          showFeedback('Promo code created successfully', 'success');
          setIsModalOpen(false);
        } else {
          setModalError((resultAction.payload as string) || 'Failed to create promo code');
        }
      }
    } catch (error) {
      setModalError('An unexpected error occurred');
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteId) {
      try {
        const resultAction = await dispatch(deletePromoCode(deleteId));
        if (deletePromoCode.fulfilled.match(resultAction)) {
          showFeedback('Promo code deleted successfully', 'success');
        } else {
          showFeedback((resultAction.payload as string) || 'Failed to delete promo code', 'error');
        }
      } catch (err) {
        showFeedback('An unexpected error occurred', 'error');
      }
      setDeleteId(null);
    }
  };

  const openCreateModal = () => {
    setEditingPromo(null);
    setModalError(null);
    setIsModalOpen(true);
  };

  const openEditModal = (promo: PromoCode) => {
    setEditingPromo(promo);
    setModalError(null);
    setIsModalOpen(true);
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      filterable: false,
    },
    {
      field: 'code',
      headerName: 'Code',
      width: 170,
      align: 'left',
      headerAlign: 'left',
      filterOperators: customStringOperators,
    },
    {
      field: 'count',
      headerName: 'Count',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      filterable: false,
      sortable: true,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: true,
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Typography className={styles.amount}>
          $ {parseFloat(params.value).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'minDeposit',
      headerName: 'Min Deposit',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      filterOperators: customStringOperators,
      renderCell: (params) => (
        <Typography className={styles.minDeposit}>$ {params.value.toLocaleString()}</Typography>
      ),
    },
    {
      field: 'siteId',
      headerName: 'Site',
      width: 140,
      align: 'left',
      headerAlign: 'left',
      filterable: false,
      sortable: false,
      valueGetter: (siteId) => sites.find((s) => s.id === siteId)?.title || siteId,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      filterable: false,
      sortable: false,
      renderCell: (params) => (
        <span className={`${styles.status} ${styles[params.value as string] || styles.active}`}>
          {params.value || 'active'}
        </span>
      ),
    },
    {
      field: 'usedCount',
      headerName: 'Used Count',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      filterable: false,
      renderCell: (params) => {
        if (params.value > 0) {
          return (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setViewingUsagesForPromo(params.row)}
              sx={{
                textTransform: 'none',
                borderRadius: '8px',
                color: '#00b3f5',
                borderColor: '#00b3f5',
                '&:hover': { borderColor: '#0090c4', color: '#0090c4' },
              }}
            >
              Users List ({params.value})
            </Button>
          );
        }
        return <Typography>{params.value}</Typography>;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 180,
      align: 'left',
      headerAlign: 'left',
      filterable: false,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      width: 180,
      align: 'left',
      headerAlign: 'left',
      filterable: false,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 240,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box className={styles.actions}>
          <Button
            size="small"
            className={styles.detailsBtn}
            disabled={params.row.usedCount === 0}
            onClick={(e) => {
              e.currentTarget.blur();
              setDetailsData({ code: params.row.code, history: params.row.usages || [] });
            }}
          >
            Details
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{
              minWidth: '40px',
              bgcolor: 'var(--accent-blue)',
              color: 'white',
              '&:hover': { bgcolor: 'var(--accent-blue-hover)' },
            }}
            onClick={(e) => {
              e.currentTarget.blur();
              openEditModal(params.row);
            }}
          >
            <Edit size={14} />
          </Button>
          <Button
            size="small"
            variant="contained"
            className={styles.deleteBtn}
            onClick={(e) => {
              e.currentTarget.blur();
              setDeleteId(params.row.id);
            }}
          >
            <Trash2 size={14} />
          </Button>
        </Box>
      ),
    },
  ];

  const renderPagination = () => (
    <CustomPagination
      count={total || 0}
      page={params.page - 1}
      onPageChange={(_, newPage) => handlePageChange(newPage + 1)}
      rowsPerPage={params.limit}
      onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
      rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
    />
  );

  if (viewingUsagesForPromo) {
    const usagesColumns: GridColDef[] = [
      {
        field: 'id',
        headerName: 'ID',
        width: 70,
        align: 'center',
        headerAlign: 'center',
        filterable: false,
      },
      {
        field: 'username',
        headerName: 'Username',
        flex: 1,
        renderCell: (params) => {
          const username = params.value || params.row.user || '-';
          const userId = params.row.userId;
          if (userId) {
            return (
              <Box
                onClick={() => navigate(`/clients/${userId}`)}
                sx={{
                  color: '#00b3f5',
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline', color: '#0090c4' },
                }}
              >
                {username}
              </Box>
            );
          }
          return <Box>{username}</Box>;
        },
      },
      {
        field: 'usedAt',
        headerName: 'Used At',
        flex: 1,
        valueGetter: (value, row) => value || row.activatedDate || row.createdAt,
        valueFormatter: (value) => (value ? formatDate(value) : '-'),
      },
    ];

    return (
      <Box className={styles.promoContainer}>
        <Box className={styles.header}>
          <Typography variant="h4" className={styles.title}>
            Usages for: {viewingUsagesForPromo.code}
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={18} />}
            onClick={() => setViewingUsagesForPromo(null)}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              color: 'var(--text-secondary)',
              borderColor: 'var(--border-color)',
              '&:hover': { borderColor: 'var(--text-primary)', color: 'var(--text-primary)' },
            }}
          >
            Back to Promocodes
          </Button>
        </Box>
        <Box className={commonStyles.gridWrapper} sx={{ mt: 2 }}>
          <DataGrid
            rows={viewingUsagesForPromo.usages || []}
            columns={usagesColumns}
            sx={commonGridStyles}
            hideFooter
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.promoContainer}>
      <Box className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Promo Codes
        </Typography>
        <Box className={styles.controls}>
          <Box className={styles.filterItem}>
            <SiteSelector
              value={params.siteId || 'all'}
              onChange={handleSiteChange}
              minWidth={180}
            />
          </Box>

          <Box className={styles.filterItem}>
            <FormControl size="small" className={styles.selectContainer}>
              <InputLabel sx={{ color: '#8698a6' }}>Status</InputLabel>
              <Select
                value={params.status || 'all'}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Button
            variant="contained"
            className={styles.createBtn}
            startIcon={<Plus size={18} />}
            onClick={(e) => {
              e.currentTarget.blur();
              openCreateModal();
            }}
          >
            Create Promo code
          </Button>
        </Box>
      </Box>

      {/* Alert Component */}
      <Alert message={feedback?.message} type={feedback?.type} />

      <Box className={commonStyles.gridWrapper}>
        {renderPagination()}
        <DataGrid
          rows={promoCodes}
          columns={columns}
          loading={isLoading}
          sx={commonGridStyles}
          filterMode="server"
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          onSortModelChange={handleSortModelChange}
        />
        {renderPagination()}
      </Box>

      {/* Delete Confirmation */}
      <ConfirmModal
        open={!!deleteId}
        title="Delete Promo Code"
        description="Are you sure you want to delete this promo code? This action cannot be undone."
        onClose={() => setDeleteId(null)}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        isDanger={true}
      />

      {/* Details Modal */}
      <PromoCodeDetailsModal
        open={!!detailsData}
        onClose={() => setDetailsData(null)}
        promoCode={detailsData?.code || ''}
        rows={detailsData?.history || []}
        theme={theme as any}
      />

      {/* Promo Code Modal (Create/Edit) */}
      <PromoCodeModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingPromo(null);
          setModalError(null);
        }}
        onSubmit={handleModalSubmit}
        sites={sites}
        theme={theme as any}
        isLoading={isCreating}
        initialData={editingPromo}
        error={modalError}
      />
    </Box>
  );
};

export default PromoCodes;
